import type { NextPage } from 'next'
import { GetStaticProps } from 'next'
import { fetchAPI } from "../../utils/api";
import TeamMemberBio from '../../components/containers/TeamMemberBio'
import WebsiteLayout from '../../components/layout/WebsiteLayout'
import { HeaderData, StrapiResponse, TeamMemberResponse, TeamMemberData, ArticleResponse, ResourceData, ArrayItemObject, ResourceListData } from '../../utils/types'
import ArticleModule from '../../components/sections/ArticleModule';
import { NextSeo } from 'next-seo';
import LoadingScreen from '../LoadingScreen';
import NotFound from '../NotFound';
import { useRouter } from 'next/router';

const ImageEndpoint = process.env.NEXT_PUBLIC_UPLOADS_URL

interface PageProps {
  teamMember: TeamMemberData,
  preview: boolean,
  articles: Array<ArrayItemObject<ResourceData>>
}

const header = {
  id: 1,
  __component: "component.header",
  title: "",
  subtitle: "",
  theme: "green",
  headerType: "hidden"
} as HeaderData

const TeamMemberBioPage: NextPage<PageProps> = ({ teamMember, preview, articles }) => {

  const router = useRouter()

  if (!router.isFallback && (teamMember === undefined || teamMember === null)) {
    return (
      <WebsiteLayout
        navbarTheme={'white'}
        header={header}
        preview={false}
        secondaryNavbar={undefined}
      >
        <NotFound />
      </WebsiteLayout>
    )
  }

  if (router.isFallback) {
    return (
      <LoadingScreen />
    )
  }

  return (
    <>
      <NextSeo
        title={`${teamMember.name}`}
        description={`${teamMember.name}, ${teamMember.jobTitle}`}
        openGraph={{
          title: teamMember.name,
          description: `${teamMember.name}, ${teamMember.jobTitle}`,
          images: teamMember.photo.data ? [
            {
              url: `${ImageEndpoint}${teamMember.photo.data.attributes.url}`
            }
          ] : undefined
        }}
      />
      <WebsiteLayout header={header} navbarTheme='blue' preview={preview}>
        <TeamMemberBio data={teamMember} articles={articles} />
      </WebsiteLayout>
    </>
  )
}


export const getStaticProps: GetStaticProps= async (context) => {

  const query = {
      filters: {
          slug: {
              // TODO figure out if there is a case where slug isn't passed as string as per type def.
              $eq: (context.params?.slug as [] || [""])
          }
      },
      populate: {
        department: {
          populate: '*'
        },
        organization: {
          populate: '*'
        },
        externalLinks: {
          populate: '*'
        },
        metaData: {
          populate: '*'
        },
        photo: {
          url: true,
          alternativeText: true
        },
      }
  }

  const articlesQuery = {
    filters: {
      $and: [
        {
          authors: {
            slug: {
              $eq: (context.params?.slug as [] || [""])
            }
          }
        },
        {
          type: {
            $in: ['Blog', 'Video', 'Audio']
          },
        }
      ]
    },
    populate: {
      authors: {
        populate: '*'
      },
      thumbnail: {
        url: true,
        alternativeText: true
      },
      category: {
        populate: '*'
      }
    }
  }

  const responseArticles = await fetchAPI<StrapiResponse<ArticleResponse>>('resources', articlesQuery)

  const response = await fetchAPI<StrapiResponse<TeamMemberResponse>>('team-members', query)

  // TODO handle lenght != 1
  const teamMember = response.data[0].attributes;
  const preview = context.preview || null
  const articles = responseArticles.data

  return {
      props: {
          teamMember,
          preview,
          articles
      }
  }

}

export async function getStaticPaths(locale:any) {
  let results = await fetchAPI<StrapiResponse<TeamMemberResponse>>('team-members')

  let paths = results.data.map((page:any) => {
      return {
          params: {
              ...page.attributes,
              slug: page.attributes.slug.split('/')[0],
              id: page.id
          },
      }
  })

  return {
      paths: paths,
      fallback: true
  };
}



export default TeamMemberBioPage
